import { Component, lazy, Suspense, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { X } from 'lucide-react'
import './map.css'

// Code-split MapScreen: Mapbox GL + Turf + TopoJSON load only when the map opens.
const MapScreen = lazy(() => import('./MapScreen').then((m) => ({ default: m.MapScreen })))

/** Full-screen hint with a guaranteed way back to Products (used for loading + load failure). */
function BackHint({ label, cta }: { label: string; cta?: string }) {
  return (
    <div className="mapscreen mapscreen--hint">
      <Link to="/products" className="mapscreen__close" aria-label="Back to products">
        <X size={18} />
      </Link>
      <div className="maphint">
        <p>{label}</p>
        {cta && (
          <Link to="/products" className="mapbtn">
            {cta}
          </Link>
        )}
      </div>
    </div>
  )
}

/** Error boundary so a failed dynamic import (offline / chunk error) never traps the user
 *  on a blank screen — it offers a way back instead. */
class MapErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  render() {
    if (this.state.failed) {
      return <BackHint label="The map failed to load. Check your connection and try again." cta="Back to Products" />
    }
    return this.props.children
  }
}

/** The `/map` route entry — eager + tiny, so the back-link chrome is always available even
 *  while the heavy map chunk loads or if it fails. */
export function MapRoute() {
  return (
    <MapErrorBoundary>
      <Suspense fallback={<BackHint label="Loading map…" />}>
        <MapScreen />
      </Suspense>
    </MapErrorBoundary>
  )
}
